import type { PopperHeaderProps } from '@talos/kyoko';
import { Box, CurrentDocumentProvider, Divider, Grid, PopperHeader, Stack, useCurrentWindow } from '@talos/kyoko';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { POPOUT_URL } from '../Layout/tokens';
import NewWindow from '../NewWindow';
import { PopoutStyleProvider } from '../PopoutWindow/PopoutStyleProvider';
import { MarketSelectorBlotter } from './blotter/MarketSelectorBlotter';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPresetsList } from './presets/MarketSelectorPresetsList';
import {
  BASE_ADVANCED_MARKET_SELECTOR_WIDTH,
  DEFAULT_MARKET_SELECTOR_HEIGHT,
  MARKET_SELECTOR_POPOUT_WINDOW_NAME,
} from './tokens';
import { getAdditionalPanelWidthFromContributions } from './utils/enrichments';

const SPACING = 8;
export function MarketSelectorWindow({
  testID,
  isOpen,
  referenceElement,
  headerProps,
  panelTopPrefix,
  panelBottomSuffix,
  panelContentKey,
}: {
  testID?: string;
  isOpen: boolean;
  referenceElement: HTMLElement | null;
  headerProps: PopperHeaderProps | undefined;
  panelTopPrefix?: ReactNode;
  panelBottomSuffix?: ReactNode;
  panelContentKey?: string;
}) {
  const { advancedMode, enrichments, extensions, setIsOpen } = useMarketSelectorContext();
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const currentWindow = useCurrentWindow();
  const popoutWindowRef = useRef<Window | null>(null);
  const handleCloseRef = useRef<() => void>(() => {});

  // Zero deps keeps this handler stable for removeEventListener
  // handleCloseRef avoids capturing a stale handleClose in the closure
  const handlePopoutWindowKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseRef.current();
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key === 'F5' || (e.metaKey && e.key === 'r')) {
      // Prevent default refresh behavior
      e.preventDefault();
    }
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleClose = useCallback(() => {
    closePanel();
    const currentPopoutWindow = popoutWindowRef.current;
    if (currentPopoutWindow == null) {
      return;
    }
    currentPopoutWindow.removeEventListener('keydown', handlePopoutWindowKeyDown);
    popoutWindowRef.current = null;
    setPopoutWindow(null);
    currentPopoutWindow.close();
  }, [closePanel, handlePopoutWindowKeyDown]);
  handleCloseRef.current = handleClose;

  const handleOpen = useCallback(
    (popoutWindow: Window) => {
      popoutWindowRef.current = popoutWindow;
      setPopoutWindow(popoutWindow);
      popoutWindow.focus();

      popoutWindow.addEventListener('keydown', handlePopoutWindowKeyDown);
    },
    [handlePopoutWindowKeyDown]
  );

  const widthAddedByExtraColumns = getAdditionalPanelWidthFromContributions(advancedMode, [
    ...enrichments,
    ...extensions,
  ]);
  const boundingRect = referenceElement?.getBoundingClientRect();
  const windowWidth = BASE_ADVANCED_MARKET_SELECTOR_WIDTH + widthAddedByExtraColumns;
  const windowHeight = DEFAULT_MARKET_SELECTOR_HEIGHT;
  const windowLeft =
    currentWindow.screenLeft - windowWidth < 0
      ? currentWindow.screenLeft + currentWindow.outerWidth + SPACING
      : currentWindow.screenLeft - windowWidth - SPACING;
  const windowTop = boundingRect?.top ?? 0 + (boundingRect?.height ?? 0) + currentWindow.screenTop - windowHeight;

  return isOpen ? (
    <NewWindow
      url={POPOUT_URL}
      name={MARKET_SELECTOR_POPOUT_WINDOW_NAME}
      features={{ width: windowWidth, height: windowHeight, left: windowLeft, top: windowTop }}
      onUnload={handleClose}
      onBlock={handleClose}
      onOpen={handleOpen}
      title="Market Selector"
    >
      <CurrentDocumentProvider
        currentDocument={popoutWindow?.document ?? document}
        currentWindow={popoutWindow ?? window}
      >
        {/* Popouts need to be wrapped with their own StyleSheetManager implementation */}
        <PopoutStyleProvider popoutWindow={popoutWindow} includeGlobalStyles>
          <Stack h="100%">
            <PopperHeader {...headerProps} onCloseClicked={handleClose} />
            <Stack key={panelContentKey} flex={1} minHeight={0}>
              {panelTopPrefix && (
                <Box background="backgroundDropdown" borderBottom="1px solid" borderColor="backgroundDivider">
                  {panelTopPrefix}
                </Box>
              )}
              <Grid
                flex={1}
                gridTemplateColumns="200px min-content auto"
                data-testid={testID ?? 'market-selector-panel'}
                background="gray.040"
              >
                <MarketSelectorPresetsList />
                <Divider orientation="vertical" my="0" color="backgroundDivider" />
                <MarketSelectorBlotter />
              </Grid>
              {panelBottomSuffix && (
                <Box background="backgroundDropdown" borderTop="1px solid" borderColor="backgroundDivider">
                  {panelBottomSuffix}
                </Box>
              )}
            </Stack>
          </Stack>
        </PopoutStyleProvider>
      </CurrentDocumentProvider>
    </NewWindow>
  ) : null;
}
